import {
  LoginActionConst,
  SignupActionConst,
  OTPActionConst,
  PasswordOtpActionConst,
} from "../Constants/AuthConstants";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import { apiFetch, getAuthHeaders } from "../../utils/apiHelper";

export function setPendingEmailForOTP(email) {
  return {
    type: OTPActionConst.SET_PENDING_EMAIL_FOR_OTP,
    payload: email,
  };
}

export function clearPendingEmailForOTP() {
  return {
    type: OTPActionConst.CLEAR_PENDING_EMAIL_FOR_OTP,
  };
}

export function clearLoginStates() {
  return {
    type: LoginActionConst.CLEAR_LOGIN_STATES,
  };
}

export function setPendingEmailForPasswordOtp(email) {
  return {
    type: PasswordOtpActionConst.SET_PENDING_EMAIL,
    payload: email,
  };
}

export function clearPendingEmailForPasswordOtp() {
  return {
    type: PasswordOtpActionConst.CLEAR_PENDING_EMAIL,
  };
}

export function clearPasswordOtpState() {
  return {
    type: PasswordOtpActionConst.CLEAR_STATUS,
  };
}

export function setPendingOtpForPasswordReset(otp) {
  return {
    type: PasswordOtpActionConst.SET_PENDING_OTP,
    payload: otp,
  };
}

export function clearPendingOtpForPasswordReset() {
  return {
    type: PasswordOtpActionConst.CLEAR_PENDING_OTP,
  };
}

/**
 * User Login Action
 * @param {Object} fields - Login credentials
 * @param {string} fields.email - User email
 * @param {string} fields.password - User password
 * @param {Function} onSuccess - Callback function called on successful login
 * @param {Function} onError - Callback function called on login error with (error, navigate) params
 */
export const UserLogInAction = (fields, onSuccess, onError) => {
  return async (dispatch) => {
    dispatch({
      type: LoginActionConst.USER_LOGIN_REQUEST,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const sanitizedEmail = (fields.email || "").trim();

    const raw = JSON.stringify({
      email: sanitizedEmail,
      password: fields.password,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.LOGIN}`,
        requestOptions
      );
      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      const statusCode = result && typeof result.statusCode === "number"
        ? result.statusCode
        : response.status;

      if (statusCode === 200) {
        // Save login success to localStorage
        localStorage.setItem("loginSuccess", JSON.stringify(result));

        // Save the access token separately for easy access
        if (result.data && result.data.accessToken) {
          localStorage.setItem("accessToken", result.data.accessToken);
        }

        dispatch({
          type: LoginActionConst.USER_LOGIN_SUCC,
          result: result,
        });
        dispatch(clearPendingEmailForOTP());
        
        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        const messages = Array.isArray(result?.message)
          ? result.message
          : typeof result?.message === "string"
          ? [result.message]
          : [];

        const normalizedMessages = messages
          .filter((msg) => typeof msg === "string")
          .map((msg) => msg.toLowerCase());

        const hasEmailPending = normalizedMessages.some(
          (msg) =>
            msg.includes("email pending verification") ||
            msg.includes("email requires verification") ||
            msg.includes("email verification pending")
        );
        
        if (hasEmailPending) {
          dispatch(setPendingEmailForOTP(sanitizedEmail));
        } else {
          dispatch(clearPendingEmailForOTP());
        }

        dispatch({
          type: LoginActionConst.USER_LOGIN_FAIL,
          result: result,
        });
        
        // Call error callback if provided
        if (onError) {
          onError(result, sanitizedEmail, hasEmailPending);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      const errorResult = {
        statusCode: 500,
        data: null,
        message: error.message || "Network error occurred",
        timestamp: new Date().toISOString(),
      };
      dispatch({
        type: LoginActionConst.USER_LOGIN_FAIL,
        result: errorResult,
      });
      
      // Call error callback if provided
      if (onError) {
        onError(errorResult, (fields.email || "").trim(), false);
      }
    }
  };
};

/**
 * User Logout Action
 */
export const UserLogoutAction = () => {
  return async (dispatch, getState) => {
    // Remove FCM device token from backend before logout
    const fcmToken = getState().notifications?.fcmToken;
    if (fcmToken) {
      try {
        await apiFetch(
          `${BASE_URL}${API_ENDPOINTS.NOTIFICATIONS_DEVICE_TOKEN_REMOVE}`,
          {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ token: fcmToken }),
          }
        );
      } catch (e) {
        console.warn("Failed to remove FCM token:", e);
      }
    }

    // Clear localStorage
    localStorage.removeItem("loginSuccess");
    localStorage.removeItem("accessToken");

    // Clear sessionStorage if any auth data exists there
    sessionStorage.removeItem("loginSuccess");
    sessionStorage.removeItem("accessToken");

    dispatch(clearPendingEmailForOTP());
    dispatch({
      type: LoginActionConst.USER_LOGOUT,
    });
  };
};

/**
 * NonProfit Signup Action
 * @param {Object} fields - Signup credentials for non-profit
 */
export const NonProfitSignUpAction = (fields) => {
  return async (dispatch) => {
    dispatch({
      type: SignupActionConst.USER_SIGNUP_REQUEST,
    });

    // Create FormData for multipart/form-data
    const formData = new FormData();
    formData.append("email", fields.email);
    formData.append("password", fields.password);
    formData.append("organizationName", fields.organizationName);
    
    if (fields.firstName) {
      formData.append("firstName", fields.firstName);
    }
    if (fields.lastName) {
      formData.append("lastName", fields.lastName);
    }
    if (fields.ein) {
      formData.append("ein", fields.ein);
    }
    if (fields.address) {
      formData.append("address", fields.address);
    }
    if (fields.zipCode) {
      formData.append("zipCode", fields.zipCode);
    }
    if (fields.phoneNumber) {
      formData.append("phoneNumber", fields.phoneNumber);
    }
    if (fields.registrationDocuments) {
      formData.append("registrationDocuments", fields.registrationDocuments);
    }
    
    // Append serviceIds if provided (as JSON string since FormData doesn't handle arrays well)
    if (fields.serviceIds && Array.isArray(fields.serviceIds) && fields.serviceIds.length > 0) {
      formData.append("serviceIds", JSON.stringify(fields.serviceIds));
    }
    
    // Append file if provided
    if (fields.file) {
      formData.append("file", fields.file);
    }

    const requestOptions = {
      method: "POST",
      body: formData,
      redirect: "follow",
    };

    fetch(`${BASE_URL}${API_ENDPOINTS.NONPROFIT_SIGNUP}`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.statusCode === 201 || result.statusCode === 200) {
          dispatch({
            type: SignupActionConst.USER_SIGNUP_SUCC,
            result: result,
          });
        } else {
          dispatch({
            type: SignupActionConst.USER_SIGNUP_FAIL,
            result: result,
          });
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        dispatch({
          type: SignupActionConst.USER_SIGNUP_FAIL,
          result: {
            statusCode: 500,
            data: null,
            message: error.message || "Network error occurred",
            timestamp: new Date().toISOString(),
          },
        });
      });
  };
};

/**
 * User Signup Action
 * @param {Object} fields - Signup credentials
 * @param {string} fields.email - User email
 * @param {string} fields.password - User password
 * @param {string} fields.firstName - User first name
 * @param {string} fields.lastName - User last name
 * @param {number} fields.roleId - User role ID
 */
export const UserSignUpAction = (fields) => {
  return async (dispatch) => {
    dispatch({
      type: SignupActionConst.USER_SIGNUP_REQUEST,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      email: fields.email,
      password: fields.password,
      firstName: fields.firstName,
      lastName: fields.lastName,
      roleId: fields.roleId,
      registrationDocumentText: fields.registrationDocumentText,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BASE_URL}${API_ENDPOINTS.SIGNUP}`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        // Check if the response is successful based on statusCode
        if (result.statusCode === 201 || result.statusCode === 200) {
          dispatch({
            type: SignupActionConst.USER_SIGNUP_SUCC,
            result: result,
          });
        } else {
          dispatch({
            type: SignupActionConst.USER_SIGNUP_FAIL,
            result: result,
          });
        }
      })
      .catch((error) => {
        console.error("Signup error:", error);
        dispatch({
          type: SignupActionConst.USER_SIGNUP_FAIL,
          result: {
            statusCode: 500,
            data: null,
            message: error.message || "Network error occurred",
            timestamp: new Date().toISOString(),
          },
        });
      });
  };
};

/**
 * Verify Email Bypass Action
 * @param {Object} params
 * @param {string} params.email - Email to verify
 * @param {string} params.token - OTP token
 */
export const VerifyEmailBypassAction = ({ email, token }) => {
  return async (dispatch) => {
    dispatch({
      type: OTPActionConst.VERIFY_EMAIL_REQUEST,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      email,
      otp: token,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.VERIFY_EMAIL}`,
        requestOptions
      );

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      if (result.statusCode === 200) {
        dispatch({
          type: OTPActionConst.VERIFY_EMAIL_SUCCESS,
          result,
        });
      } else {
        dispatch({
          type: OTPActionConst.VERIFY_EMAIL_FAIL,
          result,
        });
      }
    } catch (error) {
      console.error("Verify email error:", error);
      dispatch({
        type: OTPActionConst.VERIFY_EMAIL_FAIL,
        result: {
          statusCode: 500,
          data: null,
          message: error.message || "Network error occurred",
          timestamp: new Date().toISOString(),
        },
      });
    }
  };
};

/**
 * Resends the email-verification OTP for an account that has signed up but not
 * yet verified its email. Self-contained thunk — uses onSuccess/onError
 * callbacks and does not depend on any auth reducer state.
 *
 * @param {Object} params
 * @param {string} params.email - Email to resend the verification code to
 * @param {Function} [params.onSuccess] - Called with the API result on success
 * @param {Function} [params.onError] - Called with the API result/error on failure
 */
export const ResendVerificationOtpAction = ({ email, onSuccess, onError }) => {
  return async () => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify({ email }),
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.RESEND_VERIFICATION_OTP}`,
        requestOptions
      );

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      if (result.statusCode === 200) {
        if (onSuccess) onSuccess(result);
      } else {
        if (onError) onError(result);
      }
    } catch (error) {
      console.error("Resend verification OTP error:", error);
      if (onError) {
        onError({
          statusCode: 500,
          data: null,
          message: error.message || "Network error occurred",
          timestamp: new Date().toISOString(),
        });
      }
    }
  };
};

export const RequestPasswordResetOtpAction = ({ email, onSuccess, onError }) => {
  return async (dispatch) => {
    dispatch({
      type: PasswordOtpActionConst.REQUEST_CODE,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const sanitizedEmail = (email || "").trim();

    const raw = JSON.stringify({
      email: sanitizedEmail,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.FORGOT_PASSWORD_OTP}`,
        requestOptions
      );

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      if (response.ok) {
        dispatch({
          type: PasswordOtpActionConst.REQUEST_CODE_SUCCESS,
          result,
          payload: sanitizedEmail,
        });
        dispatch(setPendingEmailForPasswordOtp(sanitizedEmail));
        dispatch(clearPendingOtpForPasswordReset());
        onSuccess?.(result);
      } else {
        dispatch({
          type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
          result,
        });
        onError?.(result);
      }
    } catch (error) {
      console.error("Password reset OTP request error:", error);
      const fallback = {
        statusCode: 500,
        data: null,
        message: error.message || "Network error occurred",
        timestamp: new Date().toISOString(),
      };
      dispatch({
        type: PasswordOtpActionConst.REQUEST_CODE_FAIL,
        result: fallback,
      });
      onError?.(fallback);
    }
  };
};

export const VerifyPasswordOtpAction = ({ email, otp, onSuccess, onError }) => {
  return async (dispatch) => {
    dispatch({
      type: PasswordOtpActionConst.VERIFY_REQUEST,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const sanitizedEmail = (email || "").trim();

    const raw = JSON.stringify({
      email: sanitizedEmail,
      otp: otp?.trim(),
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.VERIFY_PASSWORD_OTP}`,
        requestOptions
      );

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      if (response.ok) {
        dispatch({
          type: PasswordOtpActionConst.VERIFY_SUCCESS,
          result,
        });
        dispatch(setPendingOtpForPasswordReset(otp?.trim()));
        onSuccess?.(result);
      } else {
        dispatch({
          type: PasswordOtpActionConst.VERIFY_FAIL,
          result,
        });
        onError?.(result);
      }
    } catch (error) {
      console.error("Password OTP verification error:", error);
      const fallback = {
        statusCode: 500,
        data: null,
        message: error.message || "Network error occurred",
        timestamp: new Date().toISOString(),
      };
      dispatch({
        type: PasswordOtpActionConst.VERIFY_FAIL,
        result: fallback,
      });
      onError?.(fallback);
    }
  };
};

export const ResetPasswordWithOtpAction = ({
  email,
  otp,
  newPassword,
  onSuccess,
  onError,
}) => {
  return async (dispatch) => {
    dispatch({
      type: PasswordOtpActionConst.RESET_REQUEST,
    });

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const sanitizedEmail = (email || "").trim();

    const raw = JSON.stringify({
      email: sanitizedEmail,
      otp: otp?.trim(),
      newPassword,
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.RESET_PASSWORD_OTP}`,
        requestOptions
      );

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        result = {
          statusCode: response.status,
          data: null,
          message: "Unable to parse response",
          timestamp: new Date().toISOString(),
        };
      }

      if (response.ok) {
        dispatch({
          type: PasswordOtpActionConst.RESET_SUCCESS,
          result,
        });
        dispatch(clearPendingEmailForPasswordOtp());
        dispatch(clearPendingOtpForPasswordReset());
        onSuccess?.(result);
      } else {
        dispatch({
          type: PasswordOtpActionConst.RESET_FAIL,
          result,
        });
        onError?.(result);
      }
    } catch (error) {
      console.error("Password reset via OTP error:", error);
      const fallback = {
        statusCode: 500,
        data: null,
        message: error.message || "Network error occurred",
        timestamp: new Date().toISOString(),
      };
      dispatch({
        type: PasswordOtpActionConst.RESET_FAIL,
        result: fallback,
      });
      onError?.(fallback);
    }
  };
};

